import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Star, Quote, ChevronLeft, ChevronRight } from "lucide-react";
import useEmblaCarousel from "embla-carousel-react";
import { REVIEW_SUBMITTED_EVENT } from "./ReviewBox";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Google Reviews reais da Global Pneus (texto tal como publicado no Google).
const GOOGLE_TESTIMONIALS = [
  {
    name: "Cliente Google",
    rating: 5,
    date: "Mar 2026",
    text: "Oficina top demais, serviço de ótima qualidade, bem rápido e com preço super razoável. Têm todo o equipamento profissional. Vendem também pneus Hankook (e não só). Se eu vivesse em Santa Iria, com certeza iria só nesta oficina.",
    initials: "CG",
  },
  {
    name: "Cliente Google",
    rating: 5,
    date: "Ago 2025",
    text: "Sem dúvida foi uma fantástica experiência. A receção foi maravilhosa, o staff é impecável e prestativo. Fui mudar os pneus ao meu carro, fui super bem atendida e o serviço 5 estrelas.",
    initials: "CG",
  },
  {
    name: "Cliente Google",
    rating: 5,
    date: "Ago 2025",
    text: "Ótima experiência, sem enganos e depois de seres cliente percebes que não voltas a precisar de ir a outro lugar!",
    initials: "CG",
  },
  {
    name: "Cliente Google",
    rating: 5,
    date: "Mai 2025",
    text: "Tinha um pneu furado e vim porque me recomendaram pelo excelente serviço e preços razoáveis. Resolveram-me o problema e, após aconselhamento, decidi trocar 2 pneus. Toda a experiência do início ao fim foi 5 estrelas — equipa profissional, boa comunicação.",
    initials: "CG",
  },
  {
    name: "Cliente Google",
    rating: 5,
    date: "Abr 2025",
    text: "Quero parabenizar a oficina pelo excelente trabalho! A qualidade dos serviços, o atendimento cuidadoso e a dedicação da equipa são impressionantes. Um local em que podemos confiar de olhos fechados.",
    initials: "CG",
  },
  {
    name: "Bruno",
    rating: 5,
    date: "Jul 2024",
    text: "Serviço de excelência. Cordialidade e simpatia aliadas a preços comedidos. Mesmo carros \"mais chatos\" na questão do alinhamento/geometria, que outras casas recusam, aqui é garantido que não falha.",
    initials: "B",
  },
  {
    name: "Rui",
    rating: 5,
    date: "Jun 2021",
    text: "Excelente serviço. Quero agradecer a toda a equipa: Carlos, Sérgio, Ricardo. Profissionalismo, competência e seriedade são algumas das qualidades desta equipa. Alinhamento de direção 5 estrelas.",
    initials: "R",
  },
  {
    name: "Jorge",
    rating: 5,
    date: "Set 2019",
    text: "Bom atendimento e rápida mudança de pneus. O Sr. Carlos foi impecável e importou-se o suficiente para me alertar de uma peça que necessitava. Aconselho vivamente.",
    initials: "J",
  },
];

const GOOGLE_REVIEWS_URL = "https://share.google/95wG96Bqcm6pdBuwS";

const Stars = ({ rating = 5, size = 14 }) => (
  <div className="flex items-center gap-0.5">
    {Array.from({ length: 5 }).map((_, i) => (
      <Star
        key={i}
        size={size}
        className={i < rating ? "fill-[#FBBC05] text-[#FBBC05]" : "text-[#52525B]"}
      />
    ))}
  </div>
);

const GoogleG = () => (
  <svg width="16" height="16" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12 c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24 c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z" />
    <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657 C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z" />
    <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36 c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z" />
    <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571 c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z" />
  </svg>
);

export const Testimonials = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: "start",
    slidesToScroll: 1,
  });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [siteReviews, setSiteReviews] = useState([]);

  const fetchReviews = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/reviews?limit=50`);
      setSiteReviews(res.data || []);
    } catch (err) {
      // Network failure: keep Google placeholder reviews as fallback.
      console.warn("[Testimonials] failed to fetch site reviews:", err?.message || err);
    }
  }, []);

  useEffect(() => {
    fetchReviews();
    const handler = () => fetchReviews();
    window.addEventListener(REVIEW_SUBMITTED_EVENT, handler);
    return () => window.removeEventListener(REVIEW_SUBMITTED_EVENT, handler);
  }, [fetchReviews]);

  const formatRelative = (iso) => {
    try {
      const d = new Date(iso);
      const diffMs = Date.now() - d.getTime();
      const days = Math.max(0, Math.floor(diffMs / 86400000));
      if (days === 0) return "Hoje";
      if (days === 1) return "Há 1 dia";
      if (days < 30) return `Há ${days} dias`;
      const months = Math.floor(days / 30);
      if (months === 1) return "Há 1 mês";
      if (months < 12) return `Há ${months} meses`;
      const years = Math.floor(months / 12);
      return years === 1 ? "Há 1 ano" : `Há ${years} anos`;
    } catch {
      return "";
    }
  };

  const initialsOf = (name = "") =>
    name
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase())
      .join("");

  // Site reviews shown first, then placeholder Google reviews
  const allReviews = [
    ...siteReviews.map((r) => ({
      name: r.nome,
      rating: r.rating,
      date: formatRelative(r.created_at),
      text: r.texto,
      initials: initialsOf(r.nome) || "GP",
      source: "site",
    })),
    ...GOOGLE_TESTIMONIALS.map((t) => ({ ...t, source: "google" })),
  ];

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    const id = setInterval(() => emblaApi.scrollNext(), 5000);
    return () => {
      clearInterval(id);
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi, onSelect]);

  // Reinitialize embla when reviews list changes
  useEffect(() => {
    if (emblaApi) emblaApi.reInit();
  }, [emblaApi, siteReviews.length]);

  const scrollPrev = () => emblaApi && emblaApi.scrollPrev();
  const scrollNext = () => emblaApi && emblaApi.scrollNext();

  return (
    <section
      data-testid="testimonials-section"
      id="depoimentos"
      className="relative py-10 md:py-12 bg-[#0E2452] border-t border-[#2F568F]"
    >
      <div className="w-full px-6 md:px-12 lg:px-20">
        {/* Header — ultra compact */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <span className="section-label whitespace-nowrap">// Críticas</span>
            <a
              data-testid="testimonials-google-link"
              href={GOOGLE_REVIEWS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 hover:text-[#FF5A00] transition-colors group"
              aria-label="Ver críticas no Google"
            >
              <GoogleG />
              <span className="font-display font-bold text-lg text-white leading-none">4.7</span>
              <Stars rating={5} size={13} />
              <span className="hidden sm:inline text-[10px] uppercase tracking-widest text-[#A1A1AA] group-hover:text-[#FF5A00] ml-1">
                Google · 257 avaliações
              </span>
            </a>
          </div>

          {/* Carousel arrows */}
          <div className="flex items-center gap-2">
            <button
              data-testid="testimonials-prev-btn"
              onClick={scrollPrev}
              aria-label="Anterior"
              className="w-9 h-9 flex items-center justify-center border border-[#2F568F] text-white hover:bg-[#FF5A00] hover:border-[#FF5A00] transition-colors"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              data-testid="testimonials-next-btn"
              onClick={scrollNext}
              aria-label="Seguinte"
              className="w-9 h-9 flex items-center justify-center border border-[#2F568F] text-white hover:bg-[#FF5A00] hover:border-[#FF5A00] transition-colors"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>

        {/* Carousel */}
        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex gap-4">
            {allReviews.map((t, idx) => (
              <article
                key={`${t.source}-${t.name}-${t.date}`}
                data-testid={`testimonial-card-${idx}`}
                className="flex-[0_0_100%] sm:flex-[0_0_calc(50%-8px)] lg:flex-[0_0_calc(33.333%-11px)] bg-[#163A6D] border border-[#2F568F] p-4 hover:border-[#FF5A00] transition-colors min-w-0"
              >
                <div className="flex items-center justify-between mb-2">
                  <Stars rating={t.rating} size={13} />
                  <Quote className="w-4 h-4 text-[#FF5A00]/30" strokeWidth={1.5} />
                </div>
                <p className="text-[#E0E0E0] text-xs leading-relaxed font-body mb-3 line-clamp-3">
                  &ldquo;{t.text}&rdquo;
                </p>
                <div className="flex items-center gap-2.5 pt-2.5 border-t border-[#2F568F]">
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#FF5A00] to-[#FFB084] flex items-center justify-center text-black font-display font-bold text-[10px] flex-shrink-0">
                    {t.initials}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-semibold text-white text-[11px] uppercase tracking-wide truncate">
                      {t.name}
                    </div>
                    <div className="flex items-center gap-1.5 text-[10px] text-[#A1A1AA] font-body">
                      {t.source === "google" ? (
                        <GoogleG />
                      ) : (
                        <span className="inline-block w-3 h-3 bg-[#FF5A00] rounded-full" />
                      )}
                      <span>{t.date}</span>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>

        {/* Dots */}
        <div className="flex items-center justify-center gap-1.5 mt-5">
          {allReviews.map((r, i) => (
            <button
              key={`${r.name}-${r.date}-${i}`}
              data-testid={`testimonial-dot-${i}`}
              onClick={() => emblaApi && emblaApi.scrollTo(i)}
              aria-label={`Crítica ${i + 1}`}
              className={`h-1 transition-all duration-300 ${
                i === selectedIndex ? "w-6 bg-[#FF5A00]" : "w-3 bg-white/30 hover:bg-white/60"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
